import React, { useEffect, useState } from 'react'
import {
  FormControl,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material'
import { addDays, getUnixTime, format, parse } from 'date-fns'
import RichTextEditor from './RichTextEditor'
import '../../../styles/pages.css'
import { IDemoCreate } from 'infrastructure/api'
import { TokenDurationField } from './TokenDurationField'
import { AddCurriculaForDemo } from './AddCurriculaForDemo'
import { useNavigateTo } from 'infrastructure/routing'
import { createDemo, getDemoTypes } from 'providers/mutations'
import { stringOrNumberSort } from '../../Helpers'

export const DemoEnvironmentCreate = () => {
  const [schoolName, setSchoolName] = useState('')
  const [tokenDuration, setTokenDuration] = useState(1)
  const [message, setMessage] = useState('')
  const [curriculaMap, setCurriculaMap] = useState(new Map<string, number>())
  const [error, setError] = useState('')
  const navigate = useNavigateTo()
  const [allowedDemoTypes, setAllowedDemoTypes] = useState<string[]>([])
  const [demoType, setDemoType] = useState('')

  const createDefaultExpirationDate = () => {
    const currentDate = new Date()
    const newDate = addDays(currentDate, 180)
    return format(newDate, 'yyyy-MM-dd')
  }

  const [expirationDate, setExpirationDate] = useState(
    createDefaultExpirationDate(),
  )

  useEffect(() => {
    getDemoTypes().then((resp) =>
      setAllowedDemoTypes(resp.data.sort(stringOrNumberSort)),
    )
  }, [])

  const onSchoolNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSchoolName(e.target.value)
  }

  const addCurriculum = (title: string, productId: number) => {
    if (curriculaMap.has(title)) {
      return
    }
    setCurriculaMap(new Map(curriculaMap.set(title, productId)))
  }
  const removeCurriculum = (title: string) => {
    curriculaMap.delete(title)
    setCurriculaMap(new Map(curriculaMap))
  }

  function getCurriculaArray() {
    if (curriculaMap.size === 0) {
      return []
    }

    return Array.from(curriculaMap.values())
  }

  const onTokenDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTokenDuration(parseInt(e.target.value))
  }

  const demoTypeOnChange = (e: SelectChangeEvent<string>) => {
    setDemoType(e.target.value)
  }
  const expirationDateOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpirationDate(e.target.value)
  }

  const onSubmit = async () => {
    if (schoolName.length === 0) {
      setError('School name must be entered')
      return
    }
    if (message.length === 0) {
      setError('Welcome message must be entered')
      return
    }
    try {
      const expDate = parse(expirationDate, 'yyyy-MM-dd', new Date())
      const unixTime = getUnixTime(expDate)

      const requestBody: IDemoCreate = {
        school_name: schoolName,
        token_duration_days: tokenDuration,
        message,
        product_ids: getCurriculaArray(),
        expiration_date: unixTime,
      }
      if (demoType !== '') {
        requestBody.demo_type = demoType
      }
      const demoID = await createDemo(requestBody)
      navigate(`/demos/${demoID}/show`)
    } catch (e: any) {
      setError('Error creating demo environment ' + e)
    }
  }

  return (
    <Box component="form" sx={{ width: '70ch' }} onSubmit={onSubmit}>
      <Box m={2}>
        <TextField
          label="School Name"
          placeholder="Create a School Name to WOW the client"
          variant="outlined"
          onChange={onSchoolNameChange}
          fullWidth
          inputProps={{
            'aria-label': 'School Name',
          }}
        />
      </Box>
      <Box m={2} sx={{ display: 'flex', gap: '50px' }}>
        <TokenDurationField
          onChange={onTokenDurationChange}
          value={tokenDuration}
        />
        <FormControl fullWidth>
          <InputLabel id="demo_type_label">Demo Type</InputLabel>
          <Select
            labelId="demo_type_label"
            name="demo_type"
            value={demoType}
            onChange={demoTypeOnChange}
            label="Demo Type"
          >
            {allowedDemoTypes.map((val) => {
              return (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        <TextField
          id="expiration-date"
          label="Expiration date"
          type="date"
          defaultValue={expirationDate}
          onChange={expirationDateOnChange}
          fullWidth
        />
      </Box>
      <Box m={2}>
        <p>Welcome Message: </p>
        <RichTextEditor value={message} setValue={setMessage} />
      </Box>
      <AddCurriculaForDemo
        curriculaMap={curriculaMap}
        addCurriculum={addCurriculum}
        removeCurriculum={removeCurriculum}
      />
      <Box m={2}>
        <Button onClick={onSubmit} variant="outlined" color="secondary">
          Create Demo Environment!
        </Button>
      </Box>
      <Box m={2}>{error && <p className="error">{error}</p>} </Box>
    </Box>
  )
}

export default DemoEnvironmentCreate
